import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import { Button } from 'tea-component';
import CardCommon from '@/pages/strategic/components/CardCommon';
import MetricsBoard from '@/pages/strategic/components/MetricsBoard';
import Triangle from '@/pages/strategic/components/Triangle';
import RoundedTriangleImage from '@/pages/strategic/components/Triangle/RoundedTriangle';
import CapsuleBoard from './CapsuleBoard';
import RenderTitle from './RenderTitle';
import RichText from '@/pages/strategic/components/RichText';
import { IBusinessMetricCardConfig, TBusinessMetrics } from '../type.d';
import { TMode } from '@/pages/strategic/components/CapsuleBar/type.d';
import { DEFAULT_CAPSULE_COLORS_MAP } from '@sg/components/CapsuleBar/constants';
import { getMetricBoardRatioTextAndValue } from '@/pages/strategic/utils/business';
import { mathFormat } from '@/pages/strategic/utils/common';
import { STORAGE_KEYS } from '@sg/utils/sessionStorage';
import { sessionStorageUtils } from '@sg/utils/sessionStorage';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import './index.scss';

interface IMetricCardProps {
  coefficient?: number;
  config: IBusinessMetricCardConfig;
  capsuleMode?: TMode;
  capsuleHeight?: number;
  cardKey?: number;
  isActive?: number;
  // 是否共用一个最大值
  isSharingMaximum?: boolean;
  onClick?: () => void;
  mode?: 'send' | 'receive';
  syncIds?: Record<string, string[]>;
  syncGroupChange?: (data: any, ids: any) => void;
  // RichText related props
  showRichText?: boolean;
  richTextData?: any;
  richTextEditable?: boolean;
  richTextUserPermissions?: any;
  onRichTextSave?: (newData: any) => void;
  richTextCollapsed?: boolean;
  onRichTextToggle?: () => void;
  richTextGroup?: string;
  richTextType?: 'revenue' | 'profit'; // Type identifier for buried point tracking
  richTextAutoSavePageKey?: string; // Auto-save page key for RichText
  richTextAutoSaveId?: string | number; // Auto-save ID for RichText
  richTextSubtitle?: string; // Subtitle for RichText (e.g., "Updated on 2025-03-04")
  editable?: boolean;
  editKey?: string[];
  onSave?: (newData: any) => void;
  userPermissions?: { canEdit: boolean };
  valueFormatter?: (value: any) => string;
  onEditClick?: () => void; // Callback when edit button is clicked
  autoSaveId?: string; // ID for auto-save (required if autoSavePageKey is provided)
  autoSavePageKey?: string; // Page key for auto-save (if provided, auto-save is enabled)
  onEditingChange?: (isEditing: boolean) => void; // Callback when editing state changes
}

const TRIANGLE_SIZE = 32;
const LOCALE_FULL_SHORT_KEY_TRANS_MAP: any = {
  dbs_ytm_actual_short: 'dbs_ytm_actual',
  dbs_full_year_forecast_short: 'dbs_full_year_forecast',
  dbs_full_year_kpi_short: 'dbs_full_year_kpi',
  dbs_full_year_neutral_forecast_short: 'dbs_full_year_neutral_forecast',
  dbs_full_year_business_forecast_short: 'dbs_full_year_business_forecast',
  dbs_actual: 'dbs_actual',
  dbs_budget_to_next_milestone: 'dbs_budget_to_next_milestone',
  dbs_overall_budget: 'dbs_overall_budget',
  dbs_planned_current_milestone: 'dbs_planned_current_milestone',
  dbs_planned_next_milestone: 'dbs_planned_next_milestone',
  dbs_ytm_actual_avg: 'dbs_ytm_actual_avg',
  dbs_full_year_kpi_avg: 'dbs_full_year_kpi_avg'
};

export default function MetricCard(props: IMetricCardProps) {
  const {
    coefficient = 1,
    config,
    capsuleMode,
    capsuleHeight = 32,
    cardKey,
    isActive = 0,
    onClick,
    isSharingMaximum,
    mode,
    syncIds,
    syncGroupChange,
    showRichText = false,
    richTextData,
    richTextEditable = false,
    richTextUserPermissions,
    onRichTextSave,
    richTextCollapsed = true,
    onRichTextToggle,
    richTextGroup,
    richTextType,
    richTextAutoSavePageKey,
    richTextAutoSaveId,
    richTextSubtitle,
    editable = false,
    editKey=[],
    onSave = (newData: any) => {
      console.log('newData save', newData);
    },
    userPermissions = { canEdit: false },
    valueFormatter,
    onEditClick,
    autoSaveId,
    autoSavePageKey,
    onEditingChange,
  } = props;

  // Auto-save is enabled if autoSavePageKey is provided
  const autoSave = !!autoSavePageKey;
  const { formatMessage } = useIntl();
  const lastReportedRichTextHeight = useRef<number>(0);
  const isSender = mode === 'send';

  const getCapsuleBubbleTitle = (title: string | number, localeTitleKey: string, yearPrefix?: string): string => {
    // Legacy path: localeTitleKey is a short key like dbs_ytm_actual_short
    if (localeTitleKey && LOCALE_FULL_SHORT_KEY_TRANS_MAP[localeTitleKey]) {
      return formatMessage({ id: LOCALE_FULL_SHORT_KEY_TRANS_MAP[localeTitleKey] });
    }

    // If localeTitleKey is already a full i18n id (e.g. dbs_ytm_actual_dynamic),
    // use it directly and pass yearPrefix when available.
    if (localeTitleKey && localeTitleKey.startsWith('dbs_')) {
      if (yearPrefix) {
        return formatMessage({ id: localeTitleKey }, { yearPrefix });
      }
      return formatMessage({ id: localeTitleKey });
    }

    // New path: parse year prefix from displayed title (e.g. "25A", "26F - Neutral")
    const raw = String(title || localeTitleKey || '');

    const simpleMatch = raw.match(/^(\d{2})([AFB])$/);
    if (simpleMatch) {
      const [, yearPrefix, type] = simpleMatch;
      if (type === 'A') {
        return formatMessage({ id: 'dbs_ytm_actual_dynamic' }, { yearPrefix });
      }
      if (type === 'F') {
        return formatMessage({ id: 'dbs_full_year_forecast_dynamic' }, { yearPrefix });
      }
      return formatMessage({ id: 'dbs_full_year_kpi_dynamic' }, { yearPrefix });
    }

    const neutralMatch = raw.match(/^(\d{2})F\s*-\s*Neutral$/i);
    if (neutralMatch) {
      const [, yearPrefix] = neutralMatch;
      return formatMessage({ id: 'dbs_full_year_neutral_forecast_dynamic' }, { yearPrefix });
    }

    const businessTeamMatch = raw.match(/^(\d{2})F\s*-\s*Business Team$/i);
    if (businessTeamMatch) {
      const [, yearPrefix] = businessTeamMatch;
      return formatMessage({ id: 'dbs_full_year_business_forecast_dynamic' }, { yearPrefix });
    }

    // Fallback: just show the title as-is
    return String(title);
  };
  const syncedRichTextHeight = useMemo(() => {
    if (!richTextGroup || !syncIds) {
      return undefined;
    }
    const raw = syncIds[richTextGroup]?.[0];
    if (!raw) {
      return undefined;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [richTextGroup, syncIds]);
  const metricBoard = useRef(null);

  // 编辑状态
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({}); // Current input values (updates on change/blur)
  const [autoSaveVisible, setAutoSaveVisible] = useState(false); // Auto-save modal visibility

  // Auto-save pageKey for localStorage backup
  const pageKey = useMemo(() => {
    if (!autoSavePageKey || !autoSaveId) return '';
    return `${autoSavePageKey}_${autoSaveId}`;
  }, [autoSavePageKey, autoSaveId]);

  // Auto-save methods for localStorage backup
  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: pageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey
  } = autoSaveMethods;

  // Initial params ref for localStorage auto-save
  const initialParamsRef = useRef<{
    metricData: any;
  }>({
    metricData: {},
  });

  // Current save params for localStorage auto-save
  // Use editData if it has values, otherwise use initialParams (to track changes from initial state)
  const currentSaveParams = useMemo(() => {
    if (!autoSave || !isEditing) {
      return {
        metricData: {},
      };
    }

    // If editData has values, use it; otherwise use initialParams to track the starting state
    const metricData = (editData && Object.keys(editData).length > 0)
      ? editData
      : (initialParamsRef.current?.metricData || {});

    return {
      metricData: metricData,
    };
  }, [autoSave, isEditing, editData]);

  // Reset initialParams when exiting edit mode
  useEffect(() => {
    if (!isEditing && autoSave) {
      // Reset initialParams when exiting edit mode
      initialParamsRef.current = {
        metricData: {},
      };
    }
  }, [isEditing, autoSave]);

  // Notify parent of editing state changes
  useEffect(() => {
    if (onEditingChange) {
      onEditingChange(isEditing);
    }
  }, [isEditing, onEditingChange]);

  // Call useAutoSaveParams to save to localStorage
  useAutoSaveParams({
    pageKey: pageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: autoSave && isEditing,
    isChangeValue: true,
  });

  // Merge edit values into config for real-time capsule updates
  const displayConfig = useMemo(() => {
    if (!isEditing || Object.keys(editData).length === 0) {
      return config;
    }

    // Merge edit values into config capsule
    const updatedCapsule = config.capsule.map((capsuleItem) => {
      const updatedMetrics = capsuleItem.metrics?.map((metric) => {
        if (metric.localeTitleKey && editData[metric.localeTitleKey] !== undefined) {
          const value = editData[metric.localeTitleKey];
          // Convert to number if it's a valid numeric string
          const numValue = value === '' || value === null || value === undefined
            ? null
            : Number(value);
          return {
            ...metric,
            value: numValue !== null && !isNaN(numValue) ? numValue : metric.value
          };
        }
        return metric;
      }) || [];

      // Recalculate rates for pillar metrics based on the 'total' marker value
      const totalMetric = updatedMetrics.find(m => m.type === 'total');
      const totalValue = totalMetric?.value || 0;

      const metricsWithRates = updatedMetrics.map((metric) => {
        if (metric.type === 'pillar' && totalValue !== 0 && metric.value != null) {
          return {
            ...metric,
            rate: Math.round((metric.value / totalValue) * 100)
          };
        }
        return metric;
      });

      return {
        ...capsuleItem,
        metrics: metricsWithRates
      };
    });

    return {
      ...config,
      capsule: updatedCapsule
    };
  }, [config, editData, isEditing]);

  const {parentCapsule, childCapsule} = useMemo(() => {
    const parentCapsule = displayConfig.capsule.filter(item => item.role === 'parent' || !item.role) || [];
    const childCapsule = displayConfig.capsule.filter(item => item.role && item.role !== 'parent') || [];
    return { parentCapsule, childCapsule };
  }, [displayConfig.capsule]);

  const maxValue = useMemo(() => {
    // console.log('=== MetricCard maxValue Calculation ===');
    // console.log('Card Title:', config.title);
    // console.log('isSharingMaximum:', isSharingMaximum);
    // console.log('mode:', mode);
    if (!isSharingMaximum) {
      return undefined;
    }
    const valueArray = displayConfig.capsule.flatMap(item => item?.metrics || []).map(item => item.value);
    const absoluteValues = valueArray.map(val => Math.abs(val));
    // console.log('Absolute values:', absoluteValues);
    const calculatedMax = Math.max(...absoluteValues);
    // console.log('Calculated maxValue (shared across all capsules in THIS card):', calculatedMax);
    // console.log('=======================================');
    return calculatedMax;
  }, [isSharingMaximum, displayConfig.capsule]);

  // Enter edit mode (actual function that sets state)
  const enterEditMode = () => {
    // Capture initial values from config.cards BEFORE entering edit mode
    if (autoSave && Object.keys(initialParamsRef.current.metricData).length === 0) {
      // Extract initial values from config.cards for editable fields (editKey)
      const initialMetricData: Record<string, any> = {};
      if (editKey && editKey.length > 0 && config.cards) {
        config.cards.forEach((card) => {
          // Check if this card's title is in the editKey array
          // Include value even if it's 0 (which is a valid value)
          if (editKey.includes(card.title) && card.value !== undefined && card.value !== null) {
            initialMetricData[card.title] = card.value;
          }
        });
      }

      initialParamsRef.current = {
        metricData: JSON.parse(JSON.stringify(initialMetricData)),
      };

      // Initialize editData with initial values so currentSaveParams has data from the start
      setEditData(JSON.parse(JSON.stringify(initialMetricData)));
    } else if (!autoSave) {
      // If auto-save is not enabled, still initialize editData as empty
      setEditData({});
    }

    // Call onEditClick callback if provided (for buried point tracking)
    if (onEditClick) {
      onEditClick();
    }
    setIsEditing(true);
  };

  const handleEdit = () => {
    // Check for auto-save restore prompt if auto-save is enabled
    if (autoSave && pageKey && showRestorePrompt(pageKey)) {
      setAutoSaveVisible(true);
    } else {
      enterEditMode();
    }
  };

  // User chose to recover auto-saved content
  const handleAutoSaveConfirm = () => {
    const saveValue = getSaveParamsValue(pageKey) || {};

    // Restore from auto-save
    // Note: pageKey already includes autoSaveId, so if context changed,
    // the pageKey would be different and we wouldn't find this data
    if (saveValue.metricData && Object.keys(saveValue.metricData).length > 0) {
      // Set editData with restored values
      setEditData(saveValue.metricData);

      // Also update initialParams to reflect the restored state as the new baseline
      initialParamsRef.current = {
        metricData: JSON.parse(JSON.stringify(saveValue.metricData)),
      };
    }

    setAutoSaveVisible(false);
    // Enter edit mode after setting editData
    // Call onEditClick callback if provided (for buried point tracking)
    if (onEditClick) {
      onEditClick();
    }
    setIsEditing(true);
  };

  // User chose to discard auto-saved content
  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    if (pageKey) {
      deleteSaveParamsByPageKey(pageKey);
    }
    enterEditMode();
  };

  const handleSave = async () => {
    if (onSave) {
      // Use editData for saving (convert to numbers)
      const keyValueData: Record<string, any> = {};
      Object.keys(editData).forEach(key => {
        const value = editData[key];
        // Convert to number if it's a valid numeric string
        const numValue = value === '' || value === null || value === undefined
          ? null
          : Number(value);
        // Only include if it's a valid number or null/empty
        if (numValue !== null && !isNaN(numValue)) {
          keyValueData[key] = numValue;
        } else if (value === '' || value === null || value === undefined) {
          keyValueData[key] = null;
        }
      });
      await onSave(keyValueData);
    }

    // Clear localStorage auto-save after successful save
    if (autoSave && pageKey) {
      deleteSaveParamsByPageKey(pageKey);
    }

    setEditData({});
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Clear localStorage auto-save when canceling
    if (autoSave && pageKey) {
      deleteSaveParamsByPageKey(pageKey);
    }

    setEditData({});
    setIsEditing(false);
  };

  // Handle input value changes
  const handleMetricsBoardChange = (key: string, value: any) => {
    setEditData((prev: any) => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle input blur
  const handleMetricsBoardBlur = (key: string, value: any) => {
    setEditData((prev: any) => ({
      ...prev,
      [key]: value
    }));
  };

  // Check if there are unsaved changes for RoutePrompt
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;
    if (!editData || Object.keys(editData).length === 0) return false;

    // Compare editData with initialParams
    const initialData = initialParamsRef.current?.metricData || {};

    // If both are empty, no changes
    if (Object.keys(initialData).length === 0 && Object.keys(editData).length === 0) {
      return false;
    }

    // Compare values
    return JSON.stringify(editData) !== JSON.stringify(initialData);
  }, [isEditing, editData]);

  const canEdit = editable && userPermissions.canEdit;

  return (
    <>
    <CardCommon
      cardKey={cardKey}
      isActive={isActive}
      onClick={onClick}
      title={
        <div className="dbs-metric-card-title-wrapper">
          {RenderTitle({
            title: config.titleVariable
              ? `${config.title.includes('dbs_') ? formatMessage({ id: config.title }) : config.title}_${config.titleVariable} ${config.titleUnit === undefined ? '($)' : config.titleUnit}`
              : `${config.title.includes('dbs_') ? formatMessage({ id: config.title }) : config.title} ${config.titleUnit === undefined ? '($)' : config.titleUnit}`,
            tips: config.subTitle
          })}
          {/* 编辑按钮区域 */}
          {canEdit && !isEditing && (
            <Button
              onClick={handleEdit}
              style={{ height: '30px', minWidth: '60px' }}
              className="dbs-gradient-btn"
            >
              {formatMessage({ id: 'dbs_edit' })}
            </Button>
          )}
          {canEdit && isEditing && (
            <div className="dbs-metric-card-edit-buttons">
              <Button
                onClick={handleSave}
                style={{ height: '30px', minWidth: '76px', margin: '0' }}
                className="dbs-gradient-btn"
              >
                {formatMessage({ id: 'dbs_confirm' })}
              </Button>
              <Button
                onClick={handleCancel}
                style={{ height: '30px', minWidth: '76px', margin: '0' }}
                className="dbs-outline-btn"
              >
                {formatMessage({ id: 'dbs_cancel' })}
              </Button>
            </div>
          )}
        </div>
      }
      className='dbs-home-metric-card'
    >
      <div className='dbs-home-metric-card-overview' ref={metricBoard}>
        {
          config.cards.map(item => {
            const {key, title, value, definition, updateTime, source, precision, unit, colorType = 'skyBlue', period } = item;
            // Use editData value if available (for restored auto-save or user edits), otherwise use original value
            // Convert to number if it's a string (from localStorage restore)
            let displayValue = value;
            if (isEditing && editData[title] !== undefined) {
              const editValue = editData[title];
              // Convert string to number if needed (localStorage stores as string)
              displayValue = typeof editValue === 'string' ? Number(editValue) : editValue;
              // If conversion failed, use original value
              if (isNaN(displayValue)) {
                displayValue = value;
              }
            }
            return (
              <MetricsBoard
                key={key}
                title={title.includes('dbs_') ? formatMessage({ id: title }) : title}
                // title={title}
                isEditing={isEditing && editKey.includes(title)}
                num={displayValue}
                unit={unit}
                textList={getMetricBoardRatioTextAndValue({ unit: item.ratioUnit || '', value: item.ratioValue || '' })}
                dotColor={DEFAULT_CAPSULE_COLORS_MAP[colorType || 'skyBlue']?.point || '#5EC4F1'}
                definition={definition ? formatMessage({ id: definition }) : ''}
                updateTime={updateTime ? formatMessage({ id: updateTime }) : ''}
                source={source ? formatMessage({ id: source }) : ''}
                precision={precision}
                onChange={(value: any) => handleMetricsBoardChange(title, value)}
                onBlur={(value: any) => handleMetricsBoardBlur(title, value)}
                valueFormatter={valueFormatter}
                period={period}
              />
            );
          })
        }
      </div>
      {parentCapsule?.length ? <div
        className='dbs-home-metric-card-capsule-parent'
        style={{ paddingBottom: parentCapsule?.length === 1 && !childCapsule?.length ? '0px' : '20px' }}
      >
        {parentCapsule.map((item) => {
          // Calculate tooltip position based on number of visible bars
          const visibleBars = item.metrics?.filter(metric => !metric.isHide && metric.type === 'pillar').length || 0;
          let tooltipTop = '76%'; // default
          if (capsuleMode === 'single') {
            tooltipTop = '50%';
          } else if (visibleBars === 2) {
            tooltipTop = '58%'; // Higher position for 2 bars
          } else if (visibleBars === 3) {
            tooltipTop = '65%'; // Lower position for 3 bars
          }

          return (
            <div key={item.title}>
              {/* Bubble */}
              <div className='dbs-home-metric-card-capsule-hover' style={{top: tooltipTop}}>
                <div className='dbs-home-metric-hove-item-content'>
                  <div style={{marginRight: '30px'}}>{config.subTitle}</div>
                  <div>{config.title.includes('dbs_') ? formatMessage({ id: config.title }) : config.title}</div>
                </div>
                {item.metrics?.map((items) => {
                  const { title, value, type, colorType, localeTitleKey = '', yearPrefix } = items;
                  return !items?.isHide && type !== 'zero' && <div className='dbs-home-metric-hove-item dbs-home-metric-hove-item-content' key={title}>
                    <div style={{marginRight: '30px'}}>
                      <span style={{backgroundColor: DEFAULT_CAPSULE_COLORS_MAP[colorType || 'skyBlue']?.point || '#5EC4F1',
                        width: '8px', height: '8px', display: 'inline-block', borderRadius: '50%',marginRight: '8px', position: 'relative', top: '-2px'}}></span>
                      {getCapsuleBubbleTitle(title, localeTitleKey, yearPrefix)}
                    </div>
                    <div>
                      {valueFormatter ? valueFormatter(value) : mathFormat(value)}
                      { items?.rate && type === 'pillar' ? ` (${items?.rate}%)` : ''}
                    </div>
                  </div>;
                })
                }
              </div>
              <CapsuleBoard
                key={item.title}
                type={'parent'}
                data={item}
                coefficient={coefficient}
                capsuleMode={capsuleMode}
                capsuleHeight={capsuleHeight}
                maxValue={maxValue}
                mode={mode}
                syncIds={syncIds}
                syncGroupChange={syncGroupChange}
                valueFormatter={valueFormatter}
              />

            </div>
          );
        })}
      </div>
        : <></>
      }
      {
        childCapsule?.length ? <div className='dbs-home-metric-card-capsule-child'>
          <RoundedTriangleImage
            size={64}
            position="top-center"
            style={{
              // 这里的20是padding
              top: '-45px',
            }}
          />
          {/* <Triangle
            color={'rgba(127, 111, 241, 0.1)'}
            size={TRIANGLE_SIZE}
            position={'top-center'}
            style={{
              // 这里的20是padding
              top: `-${TRIANGLE_SIZE + 20}px`
            }}
          /> */}

          {childCapsule.map((item) => {
            // Calculate tooltip position based on number of visible bars
            const visibleBars = item.metrics?.filter(metric => !metric.isHide && metric.type === 'pillar').length || 0;
            let tooltipTop = '80%'; // default
            if (capsuleMode === 'single') {
              tooltipTop = '70%';
            } else if (visibleBars === 2) {
              tooltipTop = '63%'; // Higher position for 2 bars
            } else if (visibleBars === 3) {
              tooltipTop = '70%'; // Lower position for 3 bars
            }

            return (
              <div key={item.title} className='dbs-home-metric-card-capsule-child-item'>
                {((!item.isOpacity && !item.isDefaultFold) || ((sessionStorageUtils.get(STORAGE_KEYS.CAPSULE_BOARD_COLLAPSE_STATE, []) || []) as any[]).length > 0) && <div className='dbs-home-metric-card-capsule-hover' style={{top: tooltipTop}}>
                    <div className='dbs-home-metric-hove-item-content'>
                      <div>{item.title.includes('dbs_') ? formatMessage({ id: item.title }) : item.title}</div>
                    </div>
                    {item.metrics?.map((items, index) => {
                      const { title, value, type, colorType, localeTitleKey = '', yearPrefix } = items;
                      return !items?.isHide && type === 'pillar' && <div className='dbs-home-metric-hove-item dbs-home-metric-hove-item-content' key={title}>
                        <div style={{marginRight: '30px'}}>
                          <span style={{backgroundColor: DEFAULT_CAPSULE_COLORS_MAP[colorType || 'skyBlue']?.point || '#5EC4F1',
                            width: '8px', height: '8px', display: 'inline-block', borderRadius: '50%',marginRight: '8px',position: 'relative', top: '-2px'}}></span>
                          {getCapsuleBubbleTitle(title, localeTitleKey, yearPrefix)}
                        </div>
                        <div>
                          {valueFormatter ? valueFormatter(value) : mathFormat(value)}
                          { item?.metrics?.[index]?.rate && item?.metrics?.[index]?.rate !== 100 ? ` (${item?.metrics?.[index]?.rate}%)` : ''}
                        </div>
                      </div>;
                    })
                    }
                  </div>
                }
                <CapsuleBoard
                  key={item.title}
                  type={'child'}
                  data={item}
                  coefficient={coefficient}
                  capsuleMode={capsuleMode}
                  capsuleHeight={capsuleHeight}
                  maxValue={maxValue}
                  mode={mode}
                  syncIds={syncIds}
                  syncGroupChange={syncGroupChange}
                  valueFormatter={valueFormatter}
                />
              </div>
            );
          })
          }
        </div> : <></>
      }
      {/* RichText Section */}
      {showRichText ? (
        <RichText
          data={richTextData}
          editable={richTextEditable}
          userPermissions={richTextUserPermissions}
          onSave={onRichTextSave}
          collapsed={richTextCollapsed}
          onToggle={onRichTextToggle}
          syncedHeight={!isSender ? syncedRichTextHeight : undefined}
          notesType={richTextType}
          autoSavePageKey={richTextAutoSavePageKey}
          autoSaveId={richTextAutoSaveId}
          tipString={'Data is updated by the Strategy Management team.'}
          subtitle={richTextSubtitle}
          onHeightChange={
            isSender && richTextGroup
              ? (height) => {
                  if (!syncGroupChange) {
                    return;
                  }
                  const rounded = Math.round(height);
                  if (rounded < 0 || rounded > 2000) {
                    return;
                  }
                  if (Math.abs(rounded - lastReportedRichTextHeight.current) <= 1) {
                    return;
                  }
                  lastReportedRichTextHeight.current = rounded;
                  syncGroupChange({ group: richTextGroup } as any, [String(rounded)]);
                }
              : undefined
          }
        />
      ) : <></>}
    </CardCommon>
    {/* Auto-save recovery modal */}
    {autoSavePageKey && (
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    )}
    {/* RoutePrompt to intercept navigation when there are unsaved changes */}
    {editable && (
      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          // Save changes before navigating away
          await handleSave();
        }}
      />
    )}
  </>
  );
}
